import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles/EvCharging.module.scss';

import { OmaLummeApp, EvChargingApp } from '../../omaLummeContext';

import Box from '../Common/Box';
import Button from '../Common/Button';
import ChargerDropdown from './ChargerDropdown';
import Select from '../Common/Select';
import Loading from '../Common/Loading';
import Snackbar from '../Common/Snackbar';
import FileUpload from '../Contact/FileUpload';

import { postContactForm } from '../../actions';
import { MeteringPointOption } from '../../types/consumptionRates';
import { ContactInfo, EvChargingIssueInfo } from '../../types/contactInfo';

const ReportIssue: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatchEV = EvChargingApp.useDispatch();

  const { users } = OmaLummeApp.useState();
  const { evChargerInfo, selectedCharger } = EvChargingApp.useState();

  const [activeSite, setActiveSite] = React.useState<string>(
    evChargerInfo && evChargerInfo.sites.length > 0 ? evChargerInfo.sites[0].groupId.toString() : '',
  );
  const [formSubmitting, setFormSubmitting] = React.useState<boolean>(false);
  const [formSubmitted, setFormSubmitted] = React.useState<boolean>(false);

  const defaultForm: ContactInfo = {
    firstName: users.activeUser.userFirstName,
    lastName: users.activeUser.userLastName,
    email: users.activeUser.userEmail ?? '',
    phone: users.activeUser.userNumber ?? '',
    message: '',
  };

  const [form, setForm] = React.useState<ContactInfo>(defaultForm);

  const sites: MeteringPointOption[] = evChargerInfo
    ? evChargerInfo.sites.map((site) => {
        return { value: site.groupId.toString(), label: site.groupName };
      })
    : [];

  const selectedSite = evChargerInfo?.sites.find((site) => site.groupId.toString() === activeSite);

  React.useEffect(() => {
    setForm({
      ...form,
      firstName: users.activeUser.userFirstName,
      lastName: users.activeUser.userLastName,
      email: users.activeUser.userEmail ?? '',
      phone: users.activeUser.userNumber ?? '',
      fileAttachments: undefined,
    });
  }, [users.activeUser]);

  React.useEffect(() => {
    if (selectedSite && selectedSite.chargers.length > 0) {
      dispatchEV(['changeSelectedCharger', `${selectedSite.chargers[0].chargerId}-${selectedSite.chargers[0].sockets[0].id}`]);
    } else {
      dispatchEV(['changeSelectedCharger', '']);
    }
  }, [selectedSite]);

  React.useEffect(() => {
    setActiveSite(evChargerInfo && evChargerInfo.sites.length > 0 ? evChargerInfo.sites[0].groupId.toString() : '');
  }, [evChargerInfo]);

  const submitForm = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    setFormSubmitting(true);
    setFormSubmitted(false);

    const evChargingIssueInfo: EvChargingIssueInfo = {
      site: selectedSite?.groupName ?? '',
      chargerId: selectedCharger.split('-')[0],
      socketId: selectedCharger.split('-')[1],
    };

    const contactFormRequest = await postContactForm(form, users.activeUser.userId, evChargingIssueInfo);
    if (contactFormRequest.status === 200) {
      setFormSubmitting(false);
      setFormSubmitted(true);
      setForm(defaultForm);
    }
  };

  return (
    <div>
      <header className={`${styles.evChargingHeader} ${styles.mainHeader}`}>
        <h1 aria-live="polite">{t(`evCharging:reportIssue.title`)}</h1>
      </header>
      {!evChargerInfo && <Box>{t(`evCharging:common.noEvContract`)}</Box>}
      {evChargerInfo && (
        <Box size="twoThirds">
          <form onSubmit={(e) => submitForm(e)}>
            <div className={styles.detail}>
              <h4>{t(`evCharging:common.site`)}</h4>
              <Select options={sites} activeOption={activeSite} setActiveOption={setActiveSite} />
            </div>
            <div className={styles.detail}>
              <h4>{t(`evCharging:common.charger`)}</h4>
              {selectedSite && selectedSite.chargers.length > 0 && <ChargerDropdown site={selectedSite} allowEmpty />}
              {selectedSite && selectedSite.chargers.length === 0 && <p>{t(`evCharging:charging.noChargersAtSite`)}</p>}
            </div>
            <div className={styles.detail}>
              <h4>{t(`evCharging:reportIssue.description`)}</h4>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
            </div>
            <FileUpload form={{ form, setForm }} />
            <p>{t(`evCharging:reportIssue.disclaimer`)}</p>
            <Button className="buttonGreen" type="submit">
              {t(`evCharging:reportIssue.buttonLabel`)}
            </Button>
            {formSubmitting && <Loading size="small" />}
            {formSubmitted && (
              <Snackbar type="success" inline>
                {t(`evCharging:reportIssue.thankYou`)}
              </Snackbar>
            )}
          </form>
        </Box>
      )}
    </div>
  );
};

export default ReportIssue;
